import React, { useRef } from 'react';
import { Badge, Button, Form, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { FaCheckCircle, FaExclamationTriangle, FaFileAlt, FaFolderOpen, FaMagic, FaTimes, FaUpload } from 'react-icons/fa';
import * as models from '../../../models';
import { AI_DOCUMENT_ACCEPT } from '../aiDocumentFileTypes';

export interface StagedFileMeta {
    name: string;
    source: 'bundle' | 'manual';
    confidence?: models.AiBundleMatchConfidence;
    reason?: string;
}

export type SlotAssignments = { [slotId: string]: string | string[] };

const confidenceStyles: { [key in models.AiBundleMatchConfidence]: { bg: string; text?: string; label: string } } = {
    high: { bg: 'success', label: 'High' },
    medium: { bg: 'warning', text: 'dark', label: 'Medium' },
    low: { bg: 'danger', label: 'Low' }
};

export const slotFiles = (assignments: SlotAssignments, slotId: string): string[] => {
    const value = assignments[slotId];
    return Array.isArray(value) ? value : [];
};

interface ChipProps {
    blob: string;
    meta?: StagedFileMeta;
    onRemove: () => void;
}

export const StagedFileChip: React.FC<ChipProps> = ({ blob, meta, onRemove }) => {
    const style = meta?.confidence ? confidenceStyles[meta.confidence] : undefined;
    const chip = (
        <span
            className="d-inline-flex align-items-center bg-light border rounded px-2 py-1 me-2 mb-1 small"
            style={{ maxWidth: '100%' }}
        >
            <FaFileAlt className="text-secondary me-1 flex-shrink-0" />
            <span className="text-truncate text-dark" style={{ maxWidth: '280px' }}>{meta?.name || blob}</span>
            {style && (
                <Badge bg={style.bg} text={style.text as any} className="ms-2 flex-shrink-0" style={{ fontSize: '9px' }}>
                    {style.label}
                </Badge>
            )}
            <button
                type="button"
                className="btn btn-sm ms-2 text-white flex-shrink-0 d-inline-flex align-items-center justify-content-center"
                style={{ width: '18px', height: '18px', padding: 0 }}
                title="Remove from this slot"
                onClick={onRemove}
            >
                <FaTimes size={11} />
            </button>
        </span>
    );

    if (!meta?.reason) return chip;

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`reason-${blob}`}>{meta.reason}</Tooltip>}>
            {chip}
        </OverlayTrigger>
    );
};

interface Props {
    match: models.AiRuntimeBundleMatch;
    fileSlots: models.AiRequiredInput[];
    assignments: SlotAssignments;
    fileMeta: { [blob: string]: StagedFileMeta };
    pool: string[];
    onAssign: (blob: string, slotId: string | null) => void;
    onUploadToSlot: (files: FileList, slotId: string) => void;
    onReplaceBundle: () => void;
}

const RuntimeBundleReview: React.FC<Props> = ({ match, fileSlots, assignments, fileMeta, pool, onAssign, onUploadToSlot, onReplaceBundle }) => {
    const uploadRefs = useRef<{ [slotId: string]: HTMLInputElement | null }>({});

    const filledCount = fileSlots.filter(s => slotFiles(assignments, s.slotId).length > 0).length;
    const missingRequired = fileSlots.filter(s => s.isRequired !== false && slotFiles(assignments, s.slotId).length === 0);
    const lowConfidenceCount = fileSlots.reduce(
        (count, s) => count + slotFiles(assignments, s.slotId).filter(b => fileMeta[b]?.confidence === 'low').length,
        0
    );
    const allClear = missingRequired.length === 0 && pool.length === 0 && lowConfidenceCount === 0;

    const poolOptions = (placeholder: string) => (
        <>
            <option value="">{placeholder}</option>
            {pool.map(blob => (
                <option key={blob} value={blob}>{fileMeta[blob]?.name || blob}</option>
            ))}
        </>
    );

    return (
        <div>
            <div className={`d-flex flex-wrap align-items-center justify-content-between bg-white p-3 rounded border shadow-sm mb-3 ${allClear ? 'border-success' : 'border-warning'}`}>
                <div className="d-flex align-items-center me-3">
                    {allClear
                        ? <FaCheckCircle size={22} className="text-success me-2" />
                        : <FaExclamationTriangle size={22} className="text-warning me-2" />}
                    <div>
                        <div className="fw-bold text-dark">
                            Matched {filledCount} of {fileSlots.length} slots
                            {allClear ? ' — ready to run' : ' — please review'}
                        </div>
                        <div className="small text-muted d-flex align-items-center">
                            <FaFolderOpen className="me-1" /> {match.bundleFileName}
                            <span className="mx-2">·</span>
                            <FaMagic className="me-1" /> {match.usedAiMatching ? 'AI matched' : 'Keyword matched'}
                        </div>
                    </div>
                </div>
                <div className="d-flex flex-wrap align-items-center mt-2 mt-md-0">
                    {missingRequired.length > 0 && <Badge bg="danger" className="me-2">{missingRequired.length} required slot(s) empty</Badge>}
                    {lowConfidenceCount > 0 && <Badge bg="warning" text="dark" className="me-2">{lowConfidenceCount} low-confidence match(es)</Badge>}
                    {pool.length > 0 && <Badge bg="secondary" className="me-2">{pool.length} unassigned file(s)</Badge>}
                    <Button variant="primary" size="sm" className="fw-bold text-white" onClick={onReplaceBundle}>
                        Start over with different files
                    </Button>
                </div>
            </div>

            {match.warning && (
                <div className="alert alert-warning border-0 shadow-sm small">{match.warning}</div>
            )}

            <div className="bg-white rounded border shadow-sm overflow-hidden mb-3">
                <Table responsive className="mb-0 align-middle small">
                    <thead className="table-light text-uppercase text-muted">
                        <tr>
                            <th style={{ width: '30%' }}>Review Phase / Slot</th>
                            <th>Matched Document(s)</th>
                            <th style={{ width: '240px' }}>Fix</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fileSlots.map(slot => {
                            const files = slotFiles(assignments, slot.slotId);
                            const isRequired = slot.isRequired !== false;
                            const isMissing = files.length === 0;
                            const rowStyle: React.CSSProperties = isMissing && isRequired
                                ? { borderLeft: '4px solid #dc3545', backgroundColor: '#fdf3f4' }
                                : { borderLeft: `4px solid ${isMissing ? '#dee2e6' : '#198754'}` };

                            return (
                                <tr key={slot.slotId} style={rowStyle}>
                                    <td>
                                        <div className="fw-bold text-dark">{slot.stepTitle || slot.label}</div>
                                        {slot.stepTitle && slot.label && slot.label !== slot.stepTitle && (
                                            <div className="text-muted">{slot.label}</div>
                                        )}
                                        <Badge bg={isRequired ? 'danger' : 'secondary'} className="mt-1 text-uppercase" style={{ fontSize: '9px' }}>
                                            {isRequired ? 'Required' : 'Optional'}
                                        </Badge>
                                    </td>
                                    <td>
                                        {files.map(blob => (
                                            <StagedFileChip key={blob} blob={blob} meta={fileMeta[blob]} onRemove={() => onAssign(blob, null)} />
                                        ))}
                                        {isMissing && (
                                            <span className={isRequired ? 'text-danger fw-bold' : 'text-muted'}>
                                                {isRequired ? 'No match found — assign or upload a file' : 'No file (optional)'}
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <Form.Select
                                                size="sm"
                                                className="me-2"
                                                value=""
                                                disabled={pool.length === 0}
                                                onChange={(e: any) => e.target.value && onAssign(e.target.value, slot.slotId)}
                                            >
                                                {poolOptions(pool.length ? 'Assign unmatched file…' : 'No unmatched files')}
                                            </Form.Select>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                className="text-white"
                                                title="Upload a file from your computer"
                                                onClick={() => uploadRefs.current[slot.slotId]?.click()}
                                            >
                                                <FaUpload size={11} />
                                            </Button>
                                            <input
                                                ref={el => { uploadRefs.current[slot.slotId] = el; }}
                                                type="file"
                                                multiple
                                                accept={AI_DOCUMENT_ACCEPT}
                                                className="d-none"
                                                onChange={(e) => {
                                                    if (e.target.files?.length) onUploadToSlot(e.target.files, slot.slotId);
                                                    e.target.value = '';
                                                }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            {pool.length > 0 && (
                <div className="bg-white p-3 rounded border border-secondary shadow-sm mb-3">
                    <h6 className="fw-bold text-dark mb-1">Unassigned files from your upload</h6>
                    <p className="small text-muted mb-2">These won't be sent to the tool unless you assign them to a slot.</p>
                    {pool.map(blob => (
                        <div key={blob} className="d-flex align-items-center justify-content-between border-top py-2 small">
                            <span className="d-flex align-items-center text-truncate me-3">
                                <FaFileAlt className="text-secondary me-2 flex-shrink-0" />
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id={`pool-name-${blob}`}>
                                            <span style={{ wordBreak: 'break-all' }}>{fileMeta[blob]?.name || blob}</span>
                                        </Tooltip>
                                    }
                                >
                                    <span className="text-truncate">{fileMeta[blob]?.name || blob}</span>
                                </OverlayTrigger>
                            </span>
                            <Form.Select
                                size="sm"
                                style={{ width: '260px' }}
                                value=""
                                onChange={(e: any) => e.target.value && onAssign(blob, e.target.value)}
                            >
                                <option value="">Assign to slot…</option>
                                {fileSlots.map(s => (
                                    <option key={s.slotId} value={s.slotId}>{s.stepTitle || s.label}</option>
                                ))}
                            </Form.Select>
                        </div>
                    ))}
                </div>
            )}

            {match.skippedFiles.length > 0 && (
                <details className="small text-muted">
                    <summary>{match.skippedFiles.length} file(s) were skipped</summary>
                    <ul className="mb-0 mt-1">
                        {match.skippedFiles.map((f, i) => (
                            <li key={`${f.originalFileName}-${i}`}>{f.originalFileName} — {f.reason}</li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
};

export default RuntimeBundleReview;
